import { Component, OnInit } from '@angular/core';
import { EmployeeService } from './core/services/employees/employee.service';
import { filter, skip } from 'rxjs';
import { SubscriptionService } from './core/services/subscription/subscription.service';
import { RealtimeService } from './core/services/realtime.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppService } from './core/services/app.service';
import { EstablishmentService } from './core/services/establishments/establishment.service';
import { CampaignService } from './core/services/campaign.service';
import { CustomerService } from './core/services/customers/customer.service';
import { RouterOutlet } from '@angular/router';
import { CrewService } from './core/services/crew.service';
import { HeaderComponent } from './shell/header/header.component';
import { ModalContainerComponent } from './core/components/modal-container/modal-container.component';
import { DrawerContainerComponent } from './core/components/drawer/container/drawer-container.component';
import { ShiftService } from './core/services/shift.service';
import { NotificationContainerComponent, SpinnerComponent } from '@knalgeel/pandora';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        NotificationContainerComponent,
        DrawerContainerComponent,
        ModalContainerComponent,
        HeaderComponent,
        RouterOutlet,
        SpinnerComponent,
        Toast,
    ]
})
export class AppComponent implements OnInit {
    title = 'Site';

    initialized = toSignal(this.appService.initialized$);
    establishment = toSignal(this.appService.establishment$);
    organization = toSignal(this.appService.organization$);

    // Helper for template logging
    log(message: string, value?: unknown) {
        console.log(`[AppComponent Template] ${message}`, value ?? '');
        return true;
    }

    constructor(
        private readonly establishmentService: EstablishmentService,
        private readonly subscriptionService: SubscriptionService,
        private readonly messageService: MessageService,
        private readonly realtimeService: RealtimeService,
        private readonly employeeService: EmployeeService,
        private readonly campaignService: CampaignService,
        private readonly customerService: CustomerService,
        private readonly shiftService: ShiftService,
        private readonly crewService: CrewService,
        private readonly appService: AppService
    ) {
    }

    ngOnInit() {
        console.log('[AppComponent] ========== ngOnInit START ==========');
        console.log('[AppComponent] initialized signal:', this.initialized());
        console.log('[AppComponent] organization signal:', this.organization());
        console.log('[AppComponent] establishment signal:', this.establishment());

        // Log when initialized changes
        this.appService.initialized$.subscribe(val => {
            console.log('[AppComponent] initialized$ emitted:', val);
        });

        console.log('[AppComponent] Calling initializeNotifications...');
        this.initializeNotifications();

        console.log('[AppComponent] Calling subscriptionService.init...');
        this.subscriptionService.init();

        console.log('[AppComponent] Calling realtimeService.init...');
        this.realtimeService.init();

        console.log('[AppComponent] ========== ngOnInit END ==========');
    }

    private initializeNotifications() {
        this.employeeService.employeeCreated$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Werknemer toegevoegd',
                detail: 'De werknemer is toegevoegd aan de vestiging',
            });
        });

        this.employeeService.employeeUpdated$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Werknemer bijgewerkt',
                detail: 'De informatie van de werknemer is succesvol opgeslagen',
            });
        });

        this.employeeService.employeeDeleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Werknemer verwijderd',
                detail: 'De werknemer is verwijderd',
            });
        });


        this.appService.establishment$
            .pipe(
                filter(Boolean),
                skip(1),
            )
            .subscribe(establishment => {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Vestiging gewijzigd',
                    detail: `Vestiging ${establishment.name} geselecteerd`,
                });
            });

        this.subscriptionService.entityMutated$
            .pipe()
            .subscribe(mutation => {
                this.messageService.add({
                    severity: 'info',
                    summary: `${mutation.entityType} ${mutation.action}`,
                    detail: `${mutation.entityType} ${mutation.action.toLowerCase()}`,
                });
            });

        this.establishmentService.created$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Vestiging aangemaakt',
                detail: 'De vestiging is succesvol aangemaakt',
            });
        });

        this.establishmentService.deleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Vestiging verwijderd',
                detail: 'De vestiging is verwijderd',
            });
        });

        this.campaignService.created$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Campagne aangemaakt',
                detail: 'De campagne is aangemaakt',
            });
        });

        this.campaignService.updated$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Campagne bijgewerkt',
                detail: 'De campagne is succesvol bijgewerkt',
            });
        });

        this.campaignService.deleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Campagne verwijderd',
                detail: 'De campagne is verwijderd',
            });
        });

        this.customerService.created$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Klant aangemaakt',
                detail: 'De klant is succesvol aangemaakt',
            });
        });

        this.customerService.deleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Klant verwijderd',
                detail: 'De klant is succesvol verwijderd',
            });
        });

        this.customerService.updated$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Klant bijgewerkt',
                detail: 'De klant is succesvol bijgewerkt',
            });
        });

        this.crewService.created$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Crew aangemaakt',
                detail: 'De crew is succesvol aangemaakt',
            });
        });

        this.crewService.deleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Crew verwijderd',
                detail: 'De crew is succesvol verwijderd',
            });
        });

        this.shiftService.created$.subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Shift aangepast',
                detail: 'De shift is succesvol aangepast',
            });
        });

        this.shiftService.deleted$.subscribe(() => {
            this.messageService.add({
                severity: 'info',
                summary: 'Shift verwijderd',
                detail: 'De shift is succesvol verwijderd',
            });
        });
    }
}
